import { type MeDto, type StockItemDetailDto, type StockLevelDto } from '@bqcrm/shared';
import { Anchor, Badge, Button, Card, Drawer, Group, NumberInput, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconBuildingWarehouse, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { StockDocKindBadge } from '../../components/Badges';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { useCatalogGroups, useTrades, useWarehouses } from '../../data';
import { formatDateTime, qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { whLabel } from './common';
import { ReservationsSection } from './ReservationsSection';

const FILTERS = [
  { value: 'wszystkie', label: 'Wszystkie' },
  { value: 'na_stanie', label: 'Na stanie' },
  { value: 'zarezerwowane', label: 'Zarezerwowane' },
  { value: 'ponizej_minimum', label: 'Poniżej minimum' },
];

/** Stany i dostępność (docs/14 §3): fizycznie, zarezerwowane, dostępne, w drodze — per magazyn × produkt. */
export function StockPage({ me }: { me: MeDto }) {
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q, 300);
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [trade, setTrade] = useState<string | null>(null);
  const [filter, setFilter] = useState(() => new URLSearchParams(window.location.search).get('filtr') ?? 'na_stanie');
  const [itemId, setItemId] = useState<string | null>(null);
  const whs = useWarehouses();
  const groups = useCatalogGroups();
  const trades = useTrades();
  const params = new URLSearchParams({ filter });
  if (debounced.trim()) params.set('q', debounced.trim());
  if (warehouseId) params.set('warehouseId', warehouseId);
  if (groupId) params.set('groupId', groupId);
  if (trade) params.set('trade', trade);
  const list = useQuery({ queryKey: ['stock', params.toString()], queryFn: () => api<StockLevelDto[]>(`/stock?${params}`) });
  const seeValue = can(me, 'ceny.zakup');
  const multiWh = (whs.data ?? []).filter((w) => w.active).length > 1;
  const whCode = (id: string) => {
    const w = whs.data?.find((x) => x.id === id);
    return w ? `${w.companyCode} · ${w.code}` : '';
  };

  return (
    <>
      <PageHeader
        title="Stany magazynowe"
        description="Fizycznie na półce, zarezerwowane dla realizacji i dostępne do kolejnych rezerwacji. Rezerwacja nie zmniejsza stanu fizycznego — dopiero wydanie."
        actions={
          can(me, 'magazyn.ruchy') && (
            <Button component={Link} to="/magazyn/dokumenty?nowy=PZ" variant="light">
              Przyjmij dostawę
            </Button>
          )
        }
      />
      <Group mb="md" wrap="wrap">
        <TextInput placeholder="Szukaj: nazwa, indeks, EAN…" leftSection={<IconSearch size={16} />} value={q} onChange={(e) => setQ(e.currentTarget.value)} w={{ base: '100%', sm: 280 }} />
        {multiWh && (
          <Select
            placeholder="Wszystkie magazyny"
            clearable
            data={(whs.data ?? []).filter((w) => w.active).map((w) => ({ value: w.id, label: whLabel(w) }))}
            value={warehouseId}
            onChange={setWarehouseId}
            w={{ base: '100%', sm: 260 }}
          />
        )}
        <Select placeholder="Grupa" clearable searchable data={(groups.data ?? []).map((g) => ({ value: g.id, label: g.name }))} value={groupId} onChange={setGroupId} w={{ base: '100%', sm: 200 }} />
        <Select placeholder="Branża" clearable data={(trades.data ?? []).map((t) => ({ value: t.code, label: t.name }))} value={trade} onChange={setTrade} w={{ base: '100%', sm: 200 }} />
        <Select data={FILTERS} value={filter} onChange={(v) => v && setFilter(v)} allowDeselect={false} w={{ base: '100%', sm: 170 }} />
      </Group>
      <Card padding={0}>
        <QueryState isLoading={list.isLoading} error={list.error}>
          {list.data?.length === 0 ? (
            <EmptyState
              icon={<IconBuildingWarehouse size={24} />}
              title="Brak stanów"
              hint={filter === 'na_stanie' && !debounced ? 'Magazyn jest pusty — wprowadź stan początkowy dokumentem BO albo przyjmij dostawę (PZ).' : 'Nic nie pasuje do wyszukiwania i filtrów.'}
              action={
                filter === 'na_stanie' && !debounced && can(me, 'magazyn.ruchy') ? (
                  <Button component={Link} to="/magazyn/dokumenty?nowy=BO" variant="light">
                    Wprowadź stan początkowy (BO)
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <Table.ScrollContainer minWidth={seeValue ? 900 : 760}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Produkt</Table.Th>
                    {multiWh && <Table.Th>Magazyn</Table.Th>}
                    <Table.Th ta="right">Fizycznie</Table.Th>
                    <Table.Th ta="right">Zarezerw.</Table.Th>
                    <Table.Th ta="right">Dostępne</Table.Th>
                    <Table.Th ta="right">W drodze</Table.Th>
                    <Table.Th ta="right">Minimum</Table.Th>
                    {seeValue && <Table.Th ta="right">Wartość</Table.Th>}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {list.data?.map((l) => (
                    <Table.Tr key={`${l.warehouseId}-${l.itemId}`} className="row-link" onClick={() => setItemId(l.itemId)}>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {l.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {[l.code, l.groupName, l.location && `miejsce: ${l.location}`].filter(Boolean).join(' · ')}
                        </Text>
                      </Table.Td>
                      {multiWh && <Table.Td>{whCode(l.warehouseId)}</Table.Td>}
                      <Table.Td ta="right" className="tabular">
                        {qtyPl(l.onHand)} {l.unit}
                      </Table.Td>
                      <Table.Td ta="right" className="tabular">
                        {l.reserved ? qtyPl(l.reserved) : '—'}
                      </Table.Td>
                      <Table.Td ta="right" className="tabular">
                        <Text size="sm" fw={600} c={l.available <= 0 ? 'red' : l.belowMin ? 'orange' : undefined}>
                          {qtyPl(l.available)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right" className="tabular">
                        {l.incoming ? qtyPl(l.incoming) : '—'}
                      </Table.Td>
                      <Table.Td ta="right" className="tabular">
                        {l.minQty === null ? '—' : qtyPl(l.minQty)}
                        {l.belowMin && (
                          <Badge size="xs" color="orange" variant="light" ml={4}>
                            poniżej
                          </Badge>
                        )}
                      </Table.Td>
                      {seeValue && (
                        <Table.Td ta="right">
                          <Money gr={l.valueGr} />
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </QueryState>
      </Card>
      <ItemDrawer itemId={itemId} onClose={() => setItemId(null)} me={me} />
    </>
  );
}

/** Produkt w magazynach: stany, miejsce i minimum, historia ruchów (z dokumentami). */
function ItemDrawer({ itemId, onClose, me }: { itemId: string | null; onClose: () => void; me: MeDto }) {
  const q = useQuery({ queryKey: ['stock-item', itemId], queryFn: () => api<StockItemDetailDto>(`/stock/items/${itemId}`), enabled: !!itemId });
  const whs = useWarehouses();
  const d = q.data;
  const seeValue = can(me, 'ceny.zakup');
  return (
    <Drawer opened={!!itemId} onClose={onClose} position="right" size="xl" title={d?.item.name ?? 'Produkt'}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {d && (
          <Stack>
            <Text size="sm" c="dimmed">
              {[d.item.code, d.item.ean && `EAN ${d.item.ean}`, d.item.groupName, `jednostka: ${d.item.unit}`].filter(Boolean).join(' · ')}
            </Text>
            <SectionCard title="Stany w magazynach" padding="sm">
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Magazyn</Table.Th>
                    <Table.Th ta="right">Fizycznie</Table.Th>
                    <Table.Th ta="right">Zarezerw.</Table.Th>
                    <Table.Th ta="right">Dostępne</Table.Th>
                    <Table.Th>Miejsce / minimum</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {d.levels.map((l) => {
                    const w = whs.data?.find((x) => x.id === l.warehouseId);
                    return (
                      <Table.Tr key={l.warehouseId}>
                        <Table.Td>{w ? whLabel(w) : '—'}</Table.Td>
                        <Table.Td ta="right">{qtyPl(l.onHand)}</Table.Td>
                        <Table.Td ta="right">{qtyPl(l.reserved)}</Table.Td>
                        <Table.Td ta="right">
                          <b>{qtyPl(l.available)}</b>
                        </Table.Td>
                        <Table.Td>{can(me, 'magazyn.ruchy') ? <PlaceEditor level={l} /> : `${l.location || '—'} / ${l.minQty === null ? '—' : qtyPl(l.minQty)}`}</Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </SectionCard>
            {d.levels
              .filter((l) => l.onHand > 0 || l.reserved > 0)
              .map((l) => {
                const w = whs.data?.find((x) => x.id === l.warehouseId);
                return (
                  <div key={`r-${l.warehouseId}`}>
                    {d.levels.filter((x) => x.onHand > 0 || x.reserved > 0).length > 1 && (
                      <Text size="xs" c="dimmed" mb={4}>
                        {w ? whLabel(w) : ''}
                      </Text>
                    )}
                    <ReservationsSection warehouseId={l.warehouseId} itemId={d.item.id} unit={d.item.unit} manage={can(me, 'magazyn.rezerwacje')} />
                  </div>
                );
              })}
            <SectionCard title="Ruchy" padding="sm">
              {d.movements.length === 0 ? (
                <EmptyState compact title="Brak ruchów" />
              ) : (
                <Table.ScrollContainer minWidth={560}>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Kiedy</Table.Th>
                        <Table.Th>Dokument</Table.Th>
                        <Table.Th ta="right">Ilość</Table.Th>
                        {seeValue && <Table.Th ta="right">Cena</Table.Th>}
                        <Table.Th>Dla</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {d.movements.map((m) => (
                        <Table.Tr key={m.id}>
                          <Table.Td>
                            <Text size="xs">{formatDateTime(m.at)}</Text>
                            <Text size="xs" c="dimmed">
                              {m.byName ?? ''}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Group gap={4} wrap="nowrap">
                              <StockDocKindBadge kind={m.kind} size="xs" />
                              <Anchor component={Link} to={`/magazyn/dokumenty?dokument=${m.documentId}`} size="xs">
                                {m.documentNumber ?? 'roboczy'}
                              </Anchor>
                            </Group>
                            <Text size="xs" c="dimmed">
                              {m.warehouseCode}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="right" className="tabular">
                            <Text size="sm" c={m.quantity < 0 ? 'red' : 'green'}>
                              {m.quantity > 0 ? '+' : ''}
                              {qtyPl(m.quantity)}
                            </Text>
                          </Table.Td>
                          {seeValue && (
                            <Table.Td ta="right">
                              <Money gr={m.unitCostGr === null ? null : Math.round(m.unitCostGr)} />
                            </Table.Td>
                          )}
                          <Table.Td>
                            <Text size="xs">{m.caseNumber ?? m.teamName ?? '—'}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </SectionCard>
          </Stack>
        )}
      </QueryState>
    </Drawer>
  );
}

function PlaceEditor({ level }: { level: StockLevelDto }) {
  const [location, setLocation] = useState(level.location);
  const [minQty, setMinQty] = useState<number | null>(level.minQty);
  const qc = useQueryClient();
  const dirty = location !== level.location || minQty !== level.minQty;
  const save = useMutation({
    mutationFn: () => api(`/stock/${level.warehouseId}/${level.itemId}/place`, { method: 'PUT', body: { location, minQty } }),
    onSuccess: () => {
      notifyOk('Zapisano miejsce i minimum');
      void qc.invalidateQueries({ queryKey: ['stock'] });
      void qc.invalidateQueries({ queryKey: ['stock-item'] });
    },
    onError: notifyError,
  });
  return (
    <Group gap={4} wrap="nowrap">
      <TextInput size="xs" w={110} placeholder="miejsce" value={location} onChange={(e) => setLocation(e.currentTarget.value)} />
      <NumberInput size="xs" w={80} placeholder="min." min={0} decimalScale={3} decimalSeparator="," hideControls value={minQty ?? ''} onChange={(v) => setMinQty(v === '' || v === null ? null : Number(v))} />
      {dirty && (
        <Button size="compact-xs" onClick={() => save.mutate()} loading={save.isPending}>
          Zapisz
        </Button>
      )}
    </Group>
  );
}

